import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Code, ShoppingCart, Rocket, Shield, Search, Zap, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const WebDev = () => {
  const navigate = useNavigate();

  const goToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.querySelector('#contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      <main className="pt-16">
        <section className="py-20 bg-gradient-to-b from-slate-900 to-black">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Link
                to="/"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Retour à l'accueil</span>
              </Link>

              <h1 className="text-4xl lg:text-6xl font-bold mb-6 tracking-tight text-white">
                Développement web & e-commerce
              </h1>
              <div className="w-16 h-1 rounded-full mx-auto mb-8" style={{ backgroundColor: '#f97316' }}></div>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Sites vitrines rapides, boutiques e-commerce performantes et tunnels de conversion pensés
                pour vendre. Du design à la mise en ligne, je m'occupe de tout.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={goToContact}
                style={{ backgroundColor: '#f97316' }}
                className="hover:bg-orange-600 text-white px-8 py-3 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95"
              >
                Demander un devis
              </button>
              <button
                onClick={() => window.location.href = '/realisations/web'}
                className="px-8 py-3 rounded-full text-sm font-medium bg-white/10 hover:bg-white/20 transition-colors"
              >
                Sites réalisés
              </button>
              <a
                href="#offres"
                className="px-8 py-3 rounded-full text-sm font-medium bg-white/10 hover:bg-white/20 transition-colors"
              >
                Voir les offres
              </a>
            </div>
          </div>
        </section>

        <section
          id="offres"
          className="py-16 text-slate-900"
          style={{
            backgroundColor: '#FFFFFF',
            backgroundImage: 'radial-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px)',
            backgroundSize: '4px 4px'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 flex items-center">
              <span className="w-1 h-8 rounded-full mr-4" style={{ backgroundColor: '#f97316' }}></span>
              Offres principales
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <Code className="h-6 w-6" style={{ color: '#f97316' }} />
                  <h3 className="text-xl font-semibold">Site vitrine premium</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Une présence en ligne élégante et rapide, parfaite pour présenter votre activité,
                  vos services et vos réalisations.
                </p>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4" style={{ color: '#f97316' }} />Design sur-mesure</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4" style={{ color: '#f97316' }} />Responsive & rapide</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4" style={{ color: '#f97316' }} />SEO technique de base</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4" style={{ color: '#f97316' }} />Formulaire de contact</li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <ShoppingCart className="h-6 w-6" style={{ color: '#f97316' }} />
                  <h3 className="text-xl font-semibold">Boutique e-commerce</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Une boutique prête à vendre : catalogue, paiements sécurisés, livraison et gestion des stocks.
                </p>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4" style={{ color: '#f97316' }} />Parcours d'achat optimisé</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4" style={{ color: '#f97316' }} />Paiements & livraison</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4" style={{ color: '#f97316' }} />Gestion des produits</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4" style={{ color: '#f97316' }} />Emails transactionnels</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section
          className="relative py-16 text-slate-900"
          style={{
            backgroundColor: '#F9FAFB',
            backgroundImage: 'radial-gradient(rgba(15, 23, 42, 0.06) 1px, transparent 1px)',
            backgroundSize: '4px 4px'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 flex items-center">
              <span className="w-1 h-8 rounded-full mr-4" style={{ backgroundColor: '#f97316' }}></span>
              Ce que vous gagnez
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <Rocket className="h-6 w-6 mb-4" style={{ color: '#f97316' }} />
                <h3 className="text-lg font-semibold mb-2">Performance</h3>
                <p className="text-slate-600">Chargement rapide, images optimisées et expérience fluide.</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <Search className="h-6 w-6 mb-4" style={{ color: '#f97316' }} />
                <h3 className="text-lg font-semibold mb-2">Visibilité</h3>
                <p className="text-slate-600">Structure SEO propre pour apparaître sur Google.</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <Shield className="h-6 w-6 mb-4" style={{ color: '#f97316' }} />
                <h3 className="text-lg font-semibold mb-2">Fiabilité</h3>
                <p className="text-slate-600">Sécurité, sauvegardes et stabilité en production.</p>
              </div>
            </div>
          </div>
        </section>

        <section
          className="relative py-16 text-slate-900"
          style={{
            backgroundColor: '#FFFFFF',
            backgroundImage: 'radial-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px)',
            backgroundSize: '4px 4px'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8 flex items-center">
              <span className="w-1 h-8 rounded-full mr-4" style={{ backgroundColor: '#f97316' }}></span>
              Méthode simple & efficace
            </h2>

            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <Zap className="h-6 w-6 mb-4" style={{ color: '#f97316' }} />
                <h3 className="text-lg font-semibold mb-2">1. Brief</h3>
                <p className="text-slate-600">Objectifs, cible, contenus et inspirations.</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <Code className="h-6 w-6 mb-4" style={{ color: '#f97316' }} />
                <h3 className="text-lg font-semibold mb-2">2. Design & dev</h3>
                <p className="text-slate-600">Maquettes claires puis intégration responsive.</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <ShoppingCart className="h-6 w-6 mb-4" style={{ color: '#f97316' }} />
                <h3 className="text-lg font-semibold mb-2">3. Mise en vente</h3>
                <p className="text-slate-600">Paiement, livraison, produits, tests.</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <Rocket className="h-6 w-6 mb-4" style={{ color: '#f97316' }} />
                <h3 className="text-lg font-semibold mb-2">4. Lancement</h3>
                <p className="text-slate-600">Mise en ligne et suivi des performances.</p>
              </div>
            </div>
          </div>
        </section>

        <section
          className="relative py-16 text-slate-900"
          style={{
            backgroundColor: '#F9FAFB',
            backgroundImage: 'radial-gradient(rgba(15, 23, 42, 0.06) 1px, transparent 1px)',
            backgroundSize: '4px 4px'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl p-10 border border-slate-200 text-center shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Prêt à lancer votre site ?</h2>
              <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
                Parlez-moi de votre projet, je vous propose une solution claire, un délai réaliste
                et un budget adapté à vos objectifs.
              </p>
              <button
                onClick={goToContact}
                style={{ backgroundColor: '#f97316' }}
                className="hover:bg-orange-600 text-white px-8 py-3 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95"
              >
                Discuter du projet
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WebDev;
